import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Avatar,
    Stack,
    TextField,
    InputAdornment,
    Fab,
    Skeleton,
    IconButton,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { Add as AddIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { fetchAllUsers } from '../services/userManagementService';
import CreateUserDialog from './CreateUserDialog';
import UserDetailsModal from './UserDetailsModal';
import PersonIcon from '@mui/icons-material/Person';
import cookie from 'js-cookie';
import axios from 'axios';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';

/**
 * Enhanced Search Component
 */
const SearchBar = ({ searchTerm, onSearchChange, onClear }) => {
    return (
        <TextField
            fullWidth
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    </InputAdornment>
                ),
                endAdornment: searchTerm && (
                    <InputAdornment position="end">
                        <IconButton onClick={onClear} size="small" sx={{ p: 0.5 }}>
                            <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e5e7eb',
                    '&:hover': {
                        borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                        borderColor: '#1652f0',
                        backgroundColor: '#fff',
                    },
                },
            }}
        />
    );
};

/**
 * Loading Skeleton Component
 */
const UserSkeleton = ({ isMobile = false }) => {
    if (isMobile) {
        return (
            <Card sx={{ borderRadius: '12px', border: '1px solid #e5e7eb', mb: 2 }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Grid2 container spacing={2} alignItems="center">
                        <Grid2 size="auto">
                            <Skeleton variant="circular" width={40} height={40} />
                        </Grid2>
                        <Grid2 size="grow">
                            <Skeleton variant="text" width="60%" height={24} />
                            <Skeleton variant="text" width="80%" height={20} sx={{ mt: 0.5 }} />
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                <Skeleton variant="rounded" width={80} height={24} />
                                <Skeleton variant="rounded" width={60} height={24} />
                            </Box>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
        );
    }

    return (
        <TableRow>
            <TableCell sx={{ py: 3 }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="90%" height={16} sx={{ mt: 0.5 }} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width="80%" />
            </TableCell>
            <TableCell>
                <Skeleton variant="rounded" width={80} height={24} />
            </TableCell>
            <TableCell>
                <Skeleton variant="rounded" width={60} height={24} />
            </TableCell>
        </TableRow>
    );
};

/**
 * Enhanced User Card Component for Mobile View
 */
const UserCard = ({ user, formatRoleDisplay, onUserAction }) => {
    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    return (
        <Card
            sx={{
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    borderColor: '#1652f0',
                    transform: 'translateY(-1px)',
                },
                '&:active': {
                    transform: 'translateY(0px)',
                },
            }}
            onClick={() => onUserAction?.(user)}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Grid2 container spacing={2} alignItems="center">
                    <Grid2 size="auto">
                        <Avatar
                            sx={{
                                width: 44,
                                height: 44,
                                backgroundColor: '#1652f0',
                                color: 'white',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(22, 82, 240, 0.2)',
                            }}
                        >
                            {getInitials(user.firstName, user.lastName)}
                        </Avatar>
                    </Grid2>

                    <Grid2 size="grow">
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#0f172a',
                                fontSize: '1rem',
                                mb: 0.25,
                                lineHeight: 1.3,
                            }}
                        >
                            {user.firstName} {user.lastName}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                color: '#64748b',
                                fontSize: '0.875rem',
                                mb: 1.5,
                                fontWeight: 400,
                            }}
                        >
                            {user.email}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                label={formatRoleDisplay(user.role)}
                                size="small"
                                sx={{
                                    backgroundColor: !user.role
                                        ? '#f1f5f9'
                                        : user.isInternalUser
                                          ? '#dbeafe'
                                          : '#ecfdf5',
                                    color: !user.role
                                        ? '#64748b'
                                        : user.isInternalUser
                                          ? '#2563eb'
                                          : '#059669',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    height: '26px',
                                }}
                            />
                            <Chip
                                label={user.accountStatus}
                                size="small"
                                sx={{
                                    backgroundColor:
                                        user.accountStatus === 'ACTIVE' ? '#dcfce7' : '#fee2e2',
                                    color: user.accountStatus === 'ACTIVE' ? '#16a34a' : '#dc2626',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    height: '26px',
                                }}
                            />
                        </Stack>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

/**
 * Enhanced Empty State Component
 */
const EmptyState = ({ userType, onAddUser, canAddUser }) => {
    return (
        <Box
            sx={{
                textAlign: 'center',
                py: 8,
                px: 3,
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                }}
            >
                <PersonIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
            </Box>

            <Typography
                variant="h6"
                sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: '#374151',
                    fontSize: '1.1rem',
                }}
            >
                No {userType} users yet
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    color: '#6b7280',
                    mb: 3,
                    maxWidth: 300,
                    mx: 'auto',
                    lineHeight: 1.5,
                }}
            >
                {canAddUser
                    ? `Get started by adding your first ${userType} user to your organization`
                    : `No ${userType} users have been added to the organization yet`}
            </Typography>

            {canAddUser && (
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddUser}
                    sx={{
                        backgroundColor: '#1652f0',
                        '&:hover': { backgroundColor: '#1441cc' },
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                    }}
                >
                    Add {userType === 'internal' ? 'Internal' : 'External'} User
                </Button>
            )}
        </Box>
    );
};

/**
 * iPhone-style Compact Alphabetical Index Component
 */
const AlphabeticalIndex = ({ users, scrollToLetter, isMobile }) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');
    const [isDragging, setIsDragging] = useState(false);

    // Get available letters based on business names
    const availableLetters = useMemo(() => {
        const letters = new Set();
        users.forEach((user) => {
            const firstLetter = user.businessName?.charAt(0).toUpperCase();
            if (firstLetter && /[A-Z]/.test(firstLetter)) {
                letters.add(firstLetter);
            } else {
                letters.add('#'); // For numbers and special characters
            }
        });
        return letters;
    }, [users]);

    if (isMobile || users.length === 0) return null;

    const handleMouseDown = () => setIsDragging(true);
    const handleMouseUp = () => setIsDragging(false);

    const handleLetterClick = (letter) => {
        if (availableLetters.has(letter)) {
            scrollToLetter(letter);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '20px',
                paddingY: '4px',
                userSelect: 'none',
                position: 'fixed',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                height: 'fit-content',
                maxHeight: 'calc(100vh - 120px)',
                zIndex: 1000,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {alphabet.map((letter) => {
                const isAvailable = availableLetters.has(letter);
                return (
                    <Box
                        key={letter}
                        onClick={() => handleLetterClick(letter)}
                        sx={{
                            cursor: isAvailable ? 'pointer' : 'default',
                            width: '16px',
                            height: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isAvailable ? 1 : 0.4,
                            transition: 'background-color 0.1s ease',
                            borderRadius: '2px',
                            '&:hover': isAvailable
                                ? {
                                      backgroundColor: '#f0f0f0',
                                  }
                                : {},
                            ...(isDragging && {
                                '&:hover': {
                                    backgroundColor: '#1652f0',
                                    color: 'white',
                                },
                            }),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                lineHeight: 1,
                                color: isAvailable ? '#374151' : '#9ca3af',
                            }}
                        >
                            {letter}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Enhanced User Management Component
 */
const UserManagement = ({ userType = 'internal' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { canBusiness, isLoading: permissionsLoading } = usePermissions();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [userDetailsOpen, setUserDetailsOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const letterRefs = useRef({});

    // Check if user can add/edit users (requires USER_WRITE permission)
    const canAddUser = canBusiness(PERMISSIONS.USER_WRITE);

    useEffect(() => {
        loadUsers();
        fetchCurrentUserRole();
    }, []);

    const fetchCurrentUserRole = async () => {
        try {
            const userId = cookie.get('userId');
            const businessId = cookie.get('businessId');
            const API_URL = process.env.REACT_APP_API_URL;
            const xsrfToken = cookie.get('XSRF-TOKEN');

            if (!userId || !businessId || !xsrfToken) {
                return;
            }

            const response = await axios.get(
                `${API_URL}/business-membership/get-businesses-by-user-id/${userId}`,
                {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                }
            );

            // Find the membership that matches the current business
            const currentMembership = response.data?.find(
                (membership) => membership.businessId === businessId
            );

            if (currentMembership) {
                setCurrentUserRole(currentMembership.role);
            }
        } catch (error) {
            console.error('Error fetching current user role:', error);
        }
    };

    // Organize users by letter sections (only for external users)
    const userSections = useMemo(() => {
        let filtered = [...users];

        // Apply search filter first
        if (searchTerm.trim()) {
            filtered = filtered.filter(
                (user) =>
                    `${user.firstName} ${user.lastName}`
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.businessName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // For internal users, don't group by business - just sort by name
        if (userType === 'internal') {
            filtered.sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });

            // Return as single section for internal users
            return [{ letter: null, users: filtered }];
        }

        // For external users, sort by business name and group alphabetically
        filtered.sort((a, b) => {
            const businessA = a.businessName?.toLowerCase() || '';
            const businessB = b.businessName?.toLowerCase() || '';
            return businessA.localeCompare(businessB);
        });

        // Group by first letter of business name
        const sections = {};
        filtered.forEach((user) => {
            const firstLetter = user.businessName?.charAt(0).toUpperCase();
            const letter = firstLetter && /[A-Z]/.test(firstLetter) ? firstLetter : '#';

            if (!sections[letter]) {
                sections[letter] = [];
            }
            sections[letter].push(user);
        });

        // Sort sections alphabetically
        const sortedSections = Object.keys(sections)
            .sort((a, b) => {
                if (a === '#') return 1;
                if (b === '#') return -1;
                return a.localeCompare(b);
            })
            .map((letter) => ({
                letter,
                users: sections[letter],
            }));

        return sortedSections;
    }, [users, searchTerm, userType]);

    // Flatten for backward compatibility
    const filteredUsers = useMemo(() => {
        return userSections.flatMap((section) => section.users);
    }, [userSections]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const userData = await fetchAllUsers();

            const filteredUsers = userData.filter((user) => {
                if (userType === 'internal') {
                    return user.isInternalUser === true;
                } else {
                    return user.isInternalUser === false;
                }
            });

            setUsers(filteredUsers);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUserCreated = () => {
        setCreateDialogOpen(false);
        loadUsers();
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    const scrollToLetter = (letter) => {
        const element = letterRefs.current[letter];
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const handleUserAction = (user) => {
        setSelectedUser(user);
        setUserDetailsOpen(true);
    };

    const formatRoleDisplay = (role) => {
        if (!role) {
            return 'No Role';
        }
        return role
            .replace(/[_]/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    if (error) {
        return (
            <Box sx={{ px: { xs: 2, sm: 3 } }}>
                <Alert
                    severity="error"
                    sx={{
                        mb: 2,
                        borderRadius: '12px',
                    }}
                >
                    {error}
                </Alert>
                <Button
                    variant="contained"
                    onClick={loadUsers}
                    sx={{
                        backgroundColor: '#1652f0',
                        '&:hover': { backgroundColor: '#1441cc' },
                        textTransform: 'none',
                        borderRadius: '8px',
                    }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 2, sm: 3 }, position: 'relative' }}>
            {/* Header Section with Search */}
            <Grid2 container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                {/* Title and Description */}
                <Grid2 size={{ xs: 12, sm: isMobile ? 12 : 'grow' }}>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#0f172a',
                                mb: 1,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            }}
                        >
                            {userType === 'internal' ? 'Internal Users' : 'External Users'}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748b',
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                lineHeight: 1.5,
                                mb: { xs: 3, sm: 0 },
                            }}
                        >
                            {userType === 'internal'
                                ? 'Manage internal employees and team members'
                                : 'Manage external contractors, consultants, and partners'}
                        </Typography>
                    </Box>
                </Grid2>

                {/* Desktop Add Button - only visible with USER_WRITE permission */}
                {!isMobile && (
                    <Grid2 size="auto">
                        {permissionsLoading ? (
                            <Skeleton
                                variant="rectangular"
                                width={190}
                                height={46}
                                sx={{ borderRadius: '8px' }}
                            />
                        ) : (
                            canAddUser && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateDialogOpen(true)}
                                    sx={{
                                        backgroundColor: '#1652f0',
                                        '&:hover': { backgroundColor: '#1441cc' },
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderRadius: '8px',
                                        px: 3,
                                        py: 1.5,
                                        boxShadow: '0 2px 8px rgba(22, 82, 240, 0.2)',
                                    }}
                                >
                                    Add {userType === 'internal' ? 'Internal' : 'External'} User
                                </Button>
                            )
                        )}
                    </Grid2>
                )}

                {/* Search Bar */}
                <Grid2 size={{ xs: 12 }}>
                    <SearchBar
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        onClear={handleSearchClear}
                    />
                </Grid2>
            </Grid2>

            {/* Content Area */}
            {loading ? (
                // Loading Skeletons
                isMobile ? (
                    <Box>
                        {[...Array(3)].map((_, index) => (
                            <UserSkeleton key={index} isMobile />
                        ))}
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                                        User
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                                        Business
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                                        Role
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...Array(3)].map((_, index) => (
                                    <UserSkeleton key={index} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            ) : filteredUsers.length === 0 ? (
                // Enhanced Empty State
                <EmptyState
                    userType={userType}
                    onAddUser={() => setCreateDialogOpen(true)}
                    canAddUser={canAddUser}
                />
            ) : isMobile ? (
                // Mobile Card Layout
                <Grid2 container spacing={2}>
                    {userSections.map((section) => (
                        <React.Fragment key={section.letter || 'all'}>
                            {/* Section Header - only show for external users */}
                            {section.letter && (
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography
                                        ref={(el) => (letterRefs.current[section.letter] = el)}
                                        variant="h6"
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            backgroundColor: '#f9fafb',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            marginY: '8px',
                                        }}
                                    >
                                        {section.letter}
                                    </Typography>
                                </Grid2>
                            )}
                            {/* Section Users */}
                            {section.users.map((user) => (
                                <Grid2
                                    size={{ xs: 12 }}
                                    key={user.businessMembershipId || user.userId}
                                >
                                    <UserCard
                                        user={user}
                                        formatRoleDisplay={formatRoleDisplay}
                                        onUserAction={handleUserAction}
                                    />
                                </Grid2>
                            ))}
                        </React.Fragment>
                    ))}
                </Grid2>
            ) : (
                // Desktop/Tablet Table Layout with Alphabetical Index
                <Box sx={{ display: 'flex', gap: 0, position: 'relative' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                overflow: 'visible',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                py: 2,
                                            }}
                                        >
                                            User
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                py: 2,
                                            }}
                                        >
                                            Business
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                py: 2,
                                            }}
                                        >
                                            Role
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                py: 2,
                                            }}
                                        >
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userSections.map((section) => (
                                        <React.Fragment key={section.letter || 'all'}>
                                            {/* Section Divider - only show for external users */}
                                            {section.letter && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        ref={(el) =>
                                                            (letterRefs.current[section.letter] =
                                                                el)
                                                        }
                                                        sx={{
                                                            backgroundColor: '#f9fafb',
                                                            borderBottom: '1px solid #e5e7eb',
                                                            py: 1,
                                                            px: 3,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#6b7280',
                                                                fontSize: '0.75rem',
                                                                letterSpacing: '0.05em',
                                                            }}
                                                        >
                                                            {section.letter}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {/* Section Users */}
                                            {section.users.map((user) => (
                                                <TableRow
                                                    key={user.businessMembershipId || user.userId}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#f8fafc',
                                                        },
                                                        borderBottom: '1px solid #f1f5f9',
                                                    }}
                                                    onClick={() => handleUserAction(user)}
                                                >
                                                    <TableCell sx={{ py: 3, borderBottom: 'none' }}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#0f172a',
                                                                    fontSize: '0.875rem',
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {user.firstName} {user.lastName}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: '#64748b',
                                                                    fontSize: '0.8rem',
                                                                }}
                                                            >
                                                                {user.email}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 3, borderBottom: 'none' }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#334155',
                                                                fontSize: '0.875rem',
                                                                fontWeight: user.isInternalUser
                                                                    ? 500
                                                                    : 'bold',
                                                            }}
                                                        >
                                                            {user.businessName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 3, borderBottom: 'none' }}>
                                                        <Chip
                                                            label={formatRoleDisplay(user.role)}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: !user.role
                                                                    ? '#f1f5f9'
                                                                    : user.isInternalUser
                                                                      ? '#dbeafe'
                                                                      : '#ecfdf5',
                                                                color: !user.role
                                                                    ? '#64748b'
                                                                    : user.isInternalUser
                                                                      ? '#2563eb'
                                                                      : '#059669',
                                                                fontWeight: 500,
                                                                fontSize: '0.75rem',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                height: '24px',
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 3, borderBottom: 'none' }}>
                                                        <Chip
                                                            label={user.accountStatus}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    user.accountStatus === 'ACTIVE'
                                                                        ? '#dcfce7'
                                                                        : '#fee2e2',
                                                                color:
                                                                    user.accountStatus === 'ACTIVE'
                                                                        ? '#16a34a'
                                                                        : '#dc2626',
                                                                fontWeight: 500,
                                                                fontSize: '0.75rem',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                height: '24px',
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    {/* Alphabetical Index - Only show for external users */}
                    {userType === 'external' && (
                        <AlphabeticalIndex
                            users={filteredUsers}
                            scrollToLetter={scrollToLetter}
                            isMobile={isMobile}
                        />
                    )}
                </Box>
            )}

            {/* Mobile Floating Action Button - only visible with USER_WRITE permission */}
            {isMobile && canAddUser && (
                <Fab
                    color="primary"
                    aria-label="add user"
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#1652f0',
                        '&:hover': { backgroundColor: '#1441cc' },
                        boxShadow: '0 4px 16px rgba(22, 82, 240, 0.3)',
                        zIndex: 1000,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}

            <CreateUserDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onUserCreated={handleUserCreated}
                userType={userType}
                currentUserRole={currentUserRole}
            />

            <UserDetailsModal
                open={userDetailsOpen}
                onClose={() => setUserDetailsOpen(false)}
                user={selectedUser}
                onUserUpdated={loadUsers}
            />
        </Box>
    );
};

export default UserManagement;
